/**
 * HVN: Authentication block — editor component.
 *
 * The preview is the real server render (ServerSideRender via BlockPreview), so
 * the editor is WYSIWYG. All inputs are neutralized inside the editor by the
 * shared EditorPreview guard.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	RangeControl,
	TextControl,
} from '@wordpress/components';

import BlockPreview from '../shared/BlockPreview';

const MODE_OPTIONS = [
	{ label: __( 'Auto (form when logged out, account when logged in)', 'havenlytics' ), value: 'auto' },
	{ label: __( 'Login only', 'havenlytics' ), value: 'login' },
	{ label: __( 'Register only', 'havenlytics' ), value: 'register' },
	{ label: __( 'Login + Register tabs', 'havenlytics' ), value: 'tabs' },
];

const LAYOUT_OPTIONS = [
	{ label: __( 'Card', 'havenlytics' ), value: 'card' },
	{ label: __( 'Modern', 'havenlytics' ), value: 'modern' },
	{ label: __( 'Classic', 'havenlytics' ), value: 'classic' },
	{ label: __( 'Minimal', 'havenlytics' ), value: 'minimal' },
];

const ALIGN_OPTIONS = [
	{ label: __( 'Left', 'havenlytics' ), value: 'left' },
	{ label: __( 'Center', 'havenlytics' ), value: 'center' },
	{ label: __( 'Right', 'havenlytics' ), value: 'right' },
];

const REDIRECT_OPTIONS = [
	{ label: __( 'Current page', 'havenlytics' ), value: 'current' },
	{ label: __( 'Dashboard', 'havenlytics' ), value: 'dashboard' },
	{ label: __( 'My account (profile)', 'havenlytics' ), value: 'account' },
	{ label: __( 'Custom URL', 'havenlytics' ), value: 'custom' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		authMode,
		defaultTab,
		layout,
		cardWidth,
		cardAlign,
		showLogo,
		showHeading,
		showDescription,
		showRemember,
		showForgot,
		showRegisterLink,
		showNames,
		showTerms,
		termsUrl,
		showWelcomePanel,
		showAvatar,
		showQuickLinks,
		afterLogin,
		afterLoginUrl,
		afterRegister,
		afterRegisterUrl,
	} = attributes;

	const blockProps = useBlockProps();
	const showsRegister = authMode === 'register' || authMode === 'tabs' || authMode === 'auto';

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'General', 'havenlytics' ) } initialOpen={ true }>
					<p className="components-base-control__help">
						{ __(
							'Choose how login and registration appear on this page.',
							'havenlytics'
						) }
					</p>
					<SelectControl
						label={ __( 'Authentication mode', 'havenlytics' ) }
						value={ authMode }
						options={ MODE_OPTIONS }
						onChange={ ( value ) => setAttributes( { authMode: value } ) }
						__nextHasNoMarginBottom
					/>
					{ authMode === 'tabs' && (
						<SelectControl
							label={ __( 'Default tab', 'havenlytics' ) }
							value={ defaultTab }
							options={ [
								{ label: __( 'Login', 'havenlytics' ), value: 'login' },
								{ label: __( 'Register', 'havenlytics' ), value: 'register' },
							] }
							onChange={ ( value ) => setAttributes( { defaultTab: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
					<SelectControl
						label={ __( 'Layout style', 'havenlytics' ) }
						value={ layout }
						options={ LAYOUT_OPTIONS }
						onChange={ ( value ) => setAttributes( { layout: value } ) }
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Card width (px)', 'havenlytics' ) }
						value={ cardWidth }
						min={ 320 }
						max={ 720 }
						onChange={ ( value ) => setAttributes( { cardWidth: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Alignment', 'havenlytics' ) }
						value={ cardAlign }
						options={ ALIGN_OPTIONS }
						onChange={ ( value ) => setAttributes( { cardAlign: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Display', 'havenlytics' ) } initialOpen={ false }>
					<ToggleControl
						label={ __( 'Show logo', 'havenlytics' ) }
						checked={ !! showLogo }
						onChange={ ( value ) => setAttributes( { showLogo: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show heading', 'havenlytics' ) }
						checked={ !! showHeading }
						onChange={ ( value ) => setAttributes( { showHeading: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show description', 'havenlytics' ) }
						checked={ !! showDescription }
						onChange={ ( value ) => setAttributes( { showDescription: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show "Remember me"', 'havenlytics' ) }
						checked={ !! showRemember }
						onChange={ ( value ) => setAttributes( { showRemember: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show "Forgot password"', 'havenlytics' ) }
						checked={ !! showForgot }
						onChange={ ( value ) => setAttributes( { showForgot: value } ) }
						__nextHasNoMarginBottom
					/>
					{ ( authMode === 'login' || authMode === 'auto' ) && (
						<ToggleControl
							label={ __( 'Show register link', 'havenlytics' ) }
							checked={ !! showRegisterLink }
							onChange={ ( value ) => setAttributes( { showRegisterLink: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>

				{ showsRegister && (
					<PanelBody title={ __( 'Registration', 'havenlytics' ) } initialOpen={ false }>
						<ToggleControl
							label={ __( 'Show first / last name fields', 'havenlytics' ) }
							checked={ !! showNames }
							onChange={ ( value ) => setAttributes( { showNames: value } ) }
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Require terms checkbox', 'havenlytics' ) }
							checked={ !! showTerms }
							onChange={ ( value ) => setAttributes( { showTerms: value } ) }
							__nextHasNoMarginBottom
						/>
						{ showTerms && (
							<TextControl
								label={ __( 'Terms page URL', 'havenlytics' ) }
								value={ termsUrl }
								onChange={ ( value ) => setAttributes( { termsUrl: value } ) }
								__nextHasNoMarginBottom
							/>
						) }
					</PanelBody>
				) }

				<PanelBody title={ __( 'Logged-in panel', 'havenlytics' ) } initialOpen={ false }>
					<ToggleControl
						label={ __( 'Show welcome panel', 'havenlytics' ) }
						checked={ !! showWelcomePanel }
						onChange={ ( value ) => setAttributes( { showWelcomePanel: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show avatar', 'havenlytics' ) }
						checked={ !! showAvatar }
						onChange={ ( value ) => setAttributes( { showAvatar: value } ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show quick links', 'havenlytics' ) }
						checked={ !! showQuickLinks }
						onChange={ ( value ) => setAttributes( { showQuickLinks: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody title={ __( 'Redirects', 'havenlytics' ) } initialOpen={ false }>
					<SelectControl
						label={ __( 'After login', 'havenlytics' ) }
						value={ afterLogin }
						options={ REDIRECT_OPTIONS }
						onChange={ ( value ) => setAttributes( { afterLogin: value } ) }
						__nextHasNoMarginBottom
					/>
					{ afterLogin === 'custom' && (
						<TextControl
							label={ __( 'After login URL', 'havenlytics' ) }
							value={ afterLoginUrl }
							onChange={ ( value ) => setAttributes( { afterLoginUrl: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
					<SelectControl
						label={ __( 'After registration', 'havenlytics' ) }
						value={ afterRegister }
						options={ REDIRECT_OPTIONS }
						onChange={ ( value ) => setAttributes( { afterRegister: value } ) }
						__nextHasNoMarginBottom
					/>
					{ afterRegister === 'custom' && (
						<TextControl
							label={ __( 'After registration URL', 'havenlytics' ) }
							value={ afterRegisterUrl }
							onChange={ ( value ) => setAttributes( { afterRegisterUrl: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/authentication"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
